<template>
  <article
    class="bg-white p-2 space-y-2"
    :class="isUserPreferredRetailer && isAccountSection ? 'user-preferred-retailer' : ''"
  >
    <pdl-label v-if="isSelected && !isAccountSection" kind="prominent">
      {{ $t('fulfillment.retailer.selected') }}
    </pdl-label>
    <pdl-label v-if="isUserPreferredRetailer && isAccountSection" class="font-bold" kind="positive"
      >{{ $t('storeFinder.product.my.pref') }}
    </pdl-label>
    <pdl-heading class="retailer-card__heading" size="sm" qaid="retailer-card-name">
      {{ retailer.displayName }}
    </pdl-heading>
    <pdl-subheading v-if="hasStock" qaid="retailer-in-stock-message" class="has-stock-text">
      {{ $t('fulfillment.retailer.inStock', [retailer.retailerStock]) }}
    </pdl-subheading>
    <div qaid="retailer-card-body" class="text-xs">
      <p v-if="retailer.formattedDistance">
        {{ retailer.formattedDistance }} {{ $t('fulfillment.retailer.distance') }}
      </p>
      <p v-if="closingTime" class="mb-1">
        {{ $t('fulfillment.storePickup.selectedRetailer.openUntil', [closingTime]) }}
      </p>
      <p v-if="retailerAddress">{{ retailerAddress }}</p>
    </div>
    <div v-if="isDealerDelivery" qaid="retailer-delivery" class="text-green-140">
      <pdl-icon name="check_circle" size="18" />
      <span>{{ $t('text.retailer.delivers') }}</span>
    </div>
    <div class="buttons buttons--right">
      <pdl-button primary small qaid="retailer-card-pick-up" @click="handleRetailerAction">
        {{ retailerPickUpProperty }}
      </pdl-button>
      <pdl-link button small secondary :href="retailer.url" qaid="retailer-card-details" style="flex-grow: 1">{{
        $t('storeDetails.title')
      }}</pdl-link>
    </div>
  </article>
</template>

<script>
import {PdlLabel} from '@pedal/pdl-label';
import {PdlHeading, PdlSubheading} from '@pedal/pdl-section-header';
import {PdlButton} from '@pedal/pdl-button';
import {PdlLink} from '@pedal/pdl-link';
import {PdlIcon} from '@pedal/pdl-icon';
import dayjs from 'dayjs';
import {mapState, mapMutations} from 'vuex';
import UserOccApi from '@/api/user-occ.js';
import {PdlToastType} from '@/constants/pdl-toast-type';

export default {
  components: {PdlLabel, PdlButton, PdlLink, PdlHeading, PdlIcon, PdlSubheading},
  props: {
    retailer: {
      type: Object,
      default: () => ({}),
    },
    isSelected: {
      type: Boolean,
      default: () => false,
    },
    setSelectedRetailer: {
      type: Function,
      default: () => {},
    },
    isAccountSection: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      isLoadingRetailers: false,
    };
  },
  computed: {
    ...mapState('user', ['selectedRetailer', 'userPreferredRetailer']),
    retailerAddress() {
      return this.retailer?.address?.formattedAddress;
    },
    isUserPreferredRetailer() {
      return this.userPreferredRetailer && this.retailer && this.userPreferredRetailer.name === this.retailer.name;
    },
    isDealerDelivery() {
      const deliveryMiles = Number(this.retailer.deliveryMiles);
      const distanceMiles = this.retailer.distanceKm * 0.621371;

      return this.retailer.dealerDeliveryParticipation && distanceMiles <= deliveryMiles ? true : false;
    },
    closingTime() {
      const closingHour = this.retailer?.openingHours?.weekDayOpeningList.find(
        (listItem) => listItem.weekDay === dayjs().format('ddd')
      )?.storeClosingTime?.hourOfDay;

      if (!closingHour) return;

      const computedTime = dayjs().hour(closingHour);
      return computedTime.format('h A');
    },
    hasStock() {
      return !!this.retailer?.retailerStock;
    },
    retailerPickUpProperty() {
      return !this.isAccountSection ? this.$t('pickup.here.button') : this.$t('storeFinder.product.set.as.pref');
    },
  },
  methods: {
    ...mapMutations('user', ['setUserPreferredRetailer']),
    handleRetailerAction() {
      this.setSelectedRetailer(this.retailer);
      if (this.isAccountSection) {
        this.sendPreferredRetailer(this.retailer);
      }
    },
    async sendPreferredRetailer(retailer) {
      const payloadObj = {
        preferredRetailerCode: retailer.name,
      };
      try {
        this.isLoadingRetailers = true;
        await UserOccApi.patchUserProfile(payloadObj);
        this.setUserPreferredRetailer(retailer);
        this.setSelectedRetailer(retailer);
        this.$notify({
          type: PdlToastType.SUCCESS,
          message: window.VueI18n.t('text.account.profile.confirmationUpdated'),
        });
      } catch (error) {
        console.error(error);
        this.$notify({
          type: PdlToastType.ERROR,
          message: window.VueI18n.t('errorMessage.unableToProcess'),
        });
      } finally {
        this.isLoadingRetailers = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.has-stock-text) {
  color: var(--green-140);
}

:deep(.button.button--primary) {
  flex-grow: 1;
}

:deep(.retailer-card__heading) {
  @apply font-body;
  @apply font-bold;
}

.user-preferred-retailer {
  order: -1;
}
</style>
